import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { HANDBOOK_SHOW_MORE_LIMIT } from "pages/HandBook/const";
import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import { getHandbookTemplates, getHandbookTemplatesMore } from "pages/HandBook/model/thunks";
import { IHandbookTemplatesListFilter } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";

import { useTypedSelector } from "app/store/typedUseSelector";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

export const useHandbookTemplates = () => {
  const history = useHistory();
  const [filter, setFilter] = useState<IHandbookTemplatesListFilter>({});
  const hasListFilters = Object.keys(filter).length > 0;
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({ ...restFilter, type: "moreTemplates" } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  const editings = useTypedSelector((state) => state.handBookReducer.templateEditings)[HANDBOOK_TEMPLATE_LVL.TEMPLATES];
  const isEditing = useTypedSelector((state) => state.handBookReducer.templateEditings.isEditing);
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[key];
  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];
  const isLoadingApplyEditings = useTypedSelector((state) => state.handBookReducer.loadings)[
    "applyEditingHandbookTemplates"
  ];
  const displayTemplates = useMemo(() => {
    if (!templates?.results) return templates?.results;
    return handbooksModifyWithEditings(templates.results, editings);
  }, [templates?.results, editings]);

  useEffect(() => {
    dispatch(
      getHandbookTemplates({
        ...filter,
        limit: Math.max(templates?.results?.length ?? HANDBOOK_SHOW_MORE_LIMIT, HANDBOOK_SHOW_MORE_LIMIT) + 1,
      })
    );
  }, [cacheKey, filter]);

  const onChangeFilter = useCallback(
    (name: string, value: string) => setFilter((prev) => ({ ...prev, [name]: value })),
    []
  );

  const onRowClick = (id: number) => {
    history.push(`./templates/${id}`);
  };

  const onPagination = () => {
    dispatch(
      getHandbookTemplatesMore({ ...filter, offset: templates?.results?.length, limit: HANDBOOK_SHOW_MORE_LIMIT })
    );
  };

  const total = useMemo(
    () =>
      (templates?.count || 0) -
      Object.entries(editings.deletions).filter(([id, isDeleted]) => isDeleted && Number(id) > 0).length,
    [templates?.count, editings.deletions]
  );

  return {
    hasListFilters,
    displayTemplates,
    isLoading,
    isLoadingMore,
    isLoadingApplyEditings,
    isEditing,
    total,
    filter,
    onChangeFilter,
    onRowClick,
    onPagination,
  };
};
